import { inject, Injectable } from '@angular/core';
import { computeGrandTotal, CreateLeadParams, Lead, LeadLineItem, UpdateLeadParams } from '../models/lead.model';
import { AuthService } from './auth.service';
import { ClockSyncService } from './clock-sync.service';
import { DatabaseService, type QuerySignal } from './database.service';

/**
 * Leads created from a logged interaction (design doc §6.4). `estimated_value`
 * is always computed here from `line_items` — never trusted from the caller —
 * so it can't drift from the JSON stored in `services`.
 */
@Injectable({
  providedIn: 'root',
})
export class LeadService {
  private db = inject(DatabaseService);
  private clockSync = inject(ClockSyncService);
  private authService = inject(AuthService);

  async getById(id: string): Promise<Lead | null> {
    await this.db.init();
    const rows = await this.db.exec('SELECT * FROM leads WHERE id = ? AND deleted_at IS NULL', [id]);
    return rows.length > 0 ? (rows[0] as Lead) : null;
  }

  /** All non-deleted leads, most recently created first. */
  async getAll(): Promise<Lead[]> {
    await this.db.init();
    const rows = await this.db.exec('SELECT * FROM leads WHERE deleted_at IS NULL ORDER BY created_at DESC');
    return rows as Lead[];
  }

  async getAllByAddress(addressId: string): Promise<Lead[]> {
    await this.db.init();
    const rows = await this.db.exec(
      'SELECT * FROM leads WHERE address_id = ? AND deleted_at IS NULL ORDER BY created_at DESC',
      [addressId],
    );
    return rows as Lead[];
  }

  createQuery(): QuerySignal<Lead[]> {
    return this.db.createQuery('leads', () => this.getAll());
  }

  createQueryById(id: string): QuerySignal<Lead | null> {
    return this.db.createQuery('leads', () => this.getById(id), null);
  }

  async create(params: CreateLeadParams): Promise<Lead> {
    const userId = this.requireUser('create');
    await this.db.init();
    const id = this.db.generateId();
    const now = this.clockSync.now();
    const lineItems = params.line_items ?? [];
    const estimatedValue = lineItems.length > 0 ? computeGrandTotal(lineItems) : null;

    await this.db.run(
      `INSERT INTO leads (
         id, address_id, contact_name, contact_phone, contact_email, preferred_contact_method,
         services, estimated_value, currency, assigned_to, notes, created_by, created_at, updated_by, updated_at
       ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        params.address_id,
        params.contact_name ?? '',
        params.contact_phone ?? '',
        params.contact_email ?? '',
        params.preferred_contact_method ?? '',
        JSON.stringify(lineItems),
        estimatedValue,
        params.currency ?? 'CAD',
        params.assigned_to ?? userId,
        params.notes ?? '',
        userId,
        now,
        userId,
        now,
      ],
    );

    const created = await this.getById(id);
    if (!created) throw new Error('Failed to retrieve lead after creation');
    return created;
  }

  async update(params: UpdateLeadParams): Promise<Lead> {
    const userId = this.requireUser('update');
    await this.db.init();
    const existing = await this.getById(params.id);
    if (!existing) throw new Error(`Lead '${params.id}' not found`);

    const lineItems: LeadLineItem[] | undefined = params.line_items;
    const services = lineItems !== undefined ? JSON.stringify(lineItems) : existing.services;
    const estimatedValue =
      lineItems !== undefined ? (lineItems.length > 0 ? computeGrandTotal(lineItems) : null) : existing.estimated_value;

    const next: Lead = {
      ...existing,
      contact_name: params.contact_name ?? existing.contact_name,
      contact_phone: params.contact_phone ?? existing.contact_phone,
      contact_email: params.contact_email ?? existing.contact_email,
      preferred_contact_method: params.preferred_contact_method ?? existing.preferred_contact_method,
      services,
      estimated_value: estimatedValue,
      currency: params.currency ?? existing.currency,
      stage: params.stage ?? existing.stage,
      lost_reason_id: params.lost_reason_id ?? existing.lost_reason_id,
      assigned_to: params.assigned_to ?? existing.assigned_to,
      follow_up_at: params.follow_up_at !== undefined ? params.follow_up_at : existing.follow_up_at,
      notes: params.notes ?? existing.notes,
    };

    await this.db.run(
      `UPDATE leads SET contact_name = ?, contact_phone = ?, contact_email = ?, preferred_contact_method = ?,
         services = ?, estimated_value = ?, currency = ?, stage = ?, lost_reason_id = ?, assigned_to = ?,
         follow_up_at = ?, notes = ?, updated_by = ?, updated_at = ?
       WHERE id = ?`,
      [
        next.contact_name,
        next.contact_phone,
        next.contact_email,
        next.preferred_contact_method,
        next.services,
        next.estimated_value,
        next.currency,
        next.stage,
        next.lost_reason_id,
        next.assigned_to,
        next.follow_up_at,
        next.notes,
        userId,
        this.clockSync.now(),
        params.id,
      ],
    );

    return { ...next, updated_by: userId };
  }

  private requireUser(action: string): string {
    const userId = this.authService.getCurrentUserId();
    if (!userId) throw new Error(`User must be authenticated to ${action} a lead`);
    return userId;
  }
}
